<template>
  <div
    class="base-input-uploader"
    :class="{
      'base-input-uploader--error': Boolean(props.errorMessage) && internalFiles.length > 0,
      [`base-input-uploader--variant-${props.variant}`]: true,
    }"
    :style="uploaderStyle"
  >
    <input
      ref="fileInputRef"
      class="base-input-uploader__native-input"
      type="file"
      :multiple="props.multiple"
      :accept="acceptAttribute"
      :readonly="props.readonly"
      @change="handleFileSelect"
    >
    <template v-if="props.variant === 'dashed'">
      <div
        class="base-input-uploader__dashed"
        :class="{
          'base-input-uploader__dashed--clickable': isDashedShellClickable,
          'base-input-uploader__dashed--filled': internalFiles.length > 0,
        }"
        @drop.prevent="handleDrop"
        @click="handleDashedShellClick"
      >
        <div
          v-if="!internalFiles.length"
          class="base-input-uploader__dashed-empty"
        >
          <slot name="empty" :open-picker="handleOpenPicker">
            <BaseTypography
              type="text-secondary"
              color="var(--color-foreground-caption)"
            >
              {{ props.label }}
            </BaseTypography>
          </slot>
        </div>
        <div
          v-else
          class="base-input-uploader__dashed-filled"
        >
          <div
            v-if="props.hideImagesInList"
            class="base-input-uploader__items base-input-uploader__items--dashed"
          >
            <div
              v-for="(item, index) in visibleInternalFiles"
              :key="item.source === 'remote' ? item.remoteId : `${item.file.name}-${index}`"
              class="base-input-uploader__item"
              @click.stop="handleVisibleItemClick(item)"
            >
              <BaseUploaderFileCard
                :variant="fileCardVariant"
                :file-name="item.name"
                :file-size="item.source === 'local' ? item.file.size : item.remoteFileSize"
                :with-remove="!props.readonly"
                @remove="handleVisibleItemRemove(item)"
              />
            </div>
          </div>
          <div
            v-else
            class="base-input-uploader__items base-input-uploader__items--dashed"
          >
            <div
              v-for="(item, index) in internalFiles"
              :key="item.source === 'remote' ? item.remoteId : `${item.file.name}-${index}`"
              class="base-input-uploader__item"
              :class="{ 'base-input-uploader__item--clickable': props.withImagePreview && item.kind === 'image' }"
              @click.stop="handleItemClick(index)"
            >
              <template v-if="item.kind === 'image'">
                <div
                  class="base-input-uploader__thumb"
                  :class="{ 'base-input-uploader__thumb--previewable': props.withImagePreview }"
                >
                  <img
                    :src="item.previewUrl"
                    alt=""
                    class="base-input-uploader__thumb-img"
                  >
                  <BaseButtonIcon
                    v-if="!props.readonly"
                    class="base-input-uploader__remove base-input-uploader__remove--on-media"
                    icon-name="vuesax:close-circle-linear"
                    icon-size="12px"
                    icon-color="var(--color-foreground-light)"
                    variant="transparent-inverted"
                    size="xxs"
                    form="circle"
                    @click.stop="handleRemove(index)"
                  />
                </div>
              </template>
              <template v-else-if="item.kind === 'video'">
                <div class="base-input-uploader__thumb">
                  <video
                    :src="item.previewUrl"
                    class="base-input-uploader__thumb-video"
                    muted
                    playsinline
                  />
                  <BaseButtonIcon
                    v-if="!props.readonly"
                    class="base-input-uploader__remove base-input-uploader__remove--on-media"
                    icon-name="vuesax:close-circle-linear"
                    icon-size="12px"
                    icon-color="var(--color-foreground-light)"
                    variant="transparent"
                    size="xxs"
                    form="circle"
                    color="rgb(0 0 0 / 45%)"
                    @click.stop="handleRemove(index)"
                  />
                </div>
              </template>
              <BaseUploaderFileCard
                v-else
                :variant="fileCardVariant"
                :file-name="item.name"
                :file-size="item.source === 'local' ? item.file.size : item.remoteFileSize"
                :with-remove="!props.readonly"
                @remove="handleRemove(index)"
              />
            </div>
          </div>
        </div>
      </div>
      <BaseTypography
        v-if="props.errorMessage"
        type="text-mini-alt"
        color="var(--color-foreground-danger)"
        class="base-input-uploader__filled-error"
      >
        {{ props.errorMessage }}
      </BaseTypography>
    </template>
    <template v-else>
      <div
        v-if="!internalFiles.length"
        class="base-input-uploader__empty"
        @drop.prevent="handleDrop"
      >
        <slot name="empty" :open-picker="handleOpenPicker">
          <BaseInput
            model-value=""
            :placeholder="props.label"
            readonly
            with-full
            :disabled="props.readonly"
            :with-readonly-icon="false"
            :append-icon="props.appendIcon"
            :error-message="props.errorMessage"
            :with-error-label="Boolean(props.errorMessage)"
            size="md"
            @click="handleOpenPicker"
            @click-append-icon="handleOpenPicker"
          />
        </slot>
      </div>
      <template v-else>
        <div
          class="base-input-uploader__surface base-input-uploader__surface--filled"
          :class="{ 'base-input-uploader__surface--clickable': isFilledSurfaceClickable }"
          @drop.prevent="handleDrop"
          @click="handleFilledSurfaceClick"
        >
          <div class="base-input-uploader__filled">
            <div class="base-input-uploader__items">
              <div
                v-for="(item, index) in internalFiles"
                :key="item.source === 'remote' ? item.remoteId : `${item.file.name}-${index}`"
                class="base-input-uploader__item base-input-uploader__item--clickable"
                @click.stop="handleItemClick(index)"
              >
                <template v-if="item.kind === 'image'">
                  <div
                    class="base-input-uploader__thumb"
                    :class="{ 'base-input-uploader__thumb--previewable': props.withImagePreview }"
                  >
                    <img
                      :src="item.previewUrl"
                      alt=""
                      class="base-input-uploader__thumb-img"
                    >
                    <BaseButtonIcon
                      v-if="!props.readonly"
                      class="base-input-uploader__remove base-input-uploader__remove--on-media"
                      icon-name="vuesax:close-circle-linear"
                      icon-size="12px"
                      icon-color="var(--color-foreground-light)"
                      variant="transparent-inverted"
                      size="xxs"
                      form="circle"
                      @click.stop="handleRemove(index)"
                    />
                  </div>
                </template>
                <template v-else-if="item.kind === 'video'">
                  <div class="base-input-uploader__thumb">
                    <video
                      :src="item.previewUrl"
                      class="base-input-uploader__thumb-video"
                      muted
                      playsinline
                    />
                    <BaseButtonIcon
                      v-if="!props.readonly"
                      class="base-input-uploader__remove base-input-uploader__remove--on-media"
                      icon-name="vuesax:close-circle-linear"
                      icon-size="12px"
                      icon-color="var(--color-foreground-light)"
                      variant="transparent"
                      size="xxs"
                      form="circle"
                      color="rgb(0 0 0 / 45%)"
                      @click.stop="handleRemove(index)"
                    />
                  </div>
                </template>
                <BaseUploaderFileCard
                  v-else
                  :variant="fileCardVariant"
                  :file-name="item.name"
                  :file-size="item.source === 'local' ? item.file.size : item.remoteFileSize"
                  :with-remove="!props.readonly"
                  @remove="handleRemove(index)"
                />
              </div>
            </div>
          </div>
        </div>
        <BaseTypography
          v-if="props.errorMessage"
          type="text-mini-alt"
          color="var(--color-foreground-danger)"
          class="base-input-uploader__filled-error"
        >
          {{ props.errorMessage }}
        </BaseTypography>
      </template>
    </template>
    <BaseImageViewer
      v-if="props.withImagePreview"
      v-model:visible="imageViewerVisible"
      v-model:selected-index="imageViewerSelectedIndex"
      :src-list="imagePreviewUrls"
    />
  </div>
</template>

<script setup lang="ts">
import type {
  BaseInputUploaderModelValue,
  BaseInputUploaderRemotePayload,
  BaseInputUploaderRemoteResolve,
  BaseInputUploaderRemoteResolved,
} from './types'
import { MB } from '../../const/files'
import { useNotificationStore } from '../../lib/use/notification'
import BaseButtonIcon from '../button-icon/index.vue'
import BaseImageViewer from '../image-viewer/index.vue'
import BaseInput from '../input/index.vue'
import BaseTypography from '../typography/index.vue'
import BaseUploaderFileCard from '../uploader-file-card/index.vue'
import { isBaseInputUploaderRemotePayload } from './types'

type FileKind = 'image' | 'video' | 'file'

interface InternalLocalItem {
  source: 'local'
  file: File
  name: string
  kind: FileKind
  previewUrl: string
}

interface InternalRemoteItem {
  source: 'remote'
  remoteId: string
  name: string
  kind: FileKind
  previewUrl: string
  remoteFileSize?: number | null
}

type InternalItem = InternalLocalItem | InternalRemoteItem

interface Props {
  label?: string
  multiple?: boolean
  accept?: string
  appendIcon?: string
  maxSize?: number
  maxFiles?: number
  readonly?: boolean
  errorMessage?: string
  variant?: 'default' | 'dashed'
  height?: string
  withRemoteResolve?: BaseInputUploaderRemoteResolve
  hideImagesInList?: boolean
  withImagePreview?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  multiple: true,
  accept: '',
  appendIcon: 'vuesax:gallery-add-linear',
  readonly: false,
  variant: 'default',
  height: '120px',
  hideImagesInList: false,
  withImagePreview: false,
})

const emit = defineEmits<{
  (e: 'openItem', payload: BaseInputUploaderModelValue, index: number): void
}>()

const acceptAttribute = computed(() => {
  const value = props.accept?.trim()
  if (!value) {
    return undefined
  }
  return value
})

const modelValue = defineModel<BaseInputUploaderModelValue[]>({ required: true })

const { notify } = useNotificationStore()
const fileInputRef = ref<HTMLInputElement | null>(null)

const internalFiles = ref<InternalItem[]>([])

const imageViewerVisible = ref(false)
const imageViewerSelectedIndex = ref(0)

const imagePreviewUrls = computed(() =>
  internalFiles.value
    .filter(item => item.kind === 'image')
    .map(item => item.previewUrl)
    .filter(Boolean),
)

const visibleInternalFiles = computed(() => {
  if (!props.hideImagesInList) {
    return internalFiles.value
  }

  return internalFiles.value.filter(item => item.kind !== 'image')
})

const uploaderStyle = computed(() => ({
  '--base-input-uploader-height': props.height,
}))

const fileCardVariant = computed(() => (
  props.variant === 'dashed' ? 'secondary' : 'light'
))

const canAddMore = computed(() => {
  if (!props.maxFiles) {
    return true
  }
  return internalFiles.value.length < props.maxFiles
})

const isFilledSurfaceClickable = computed(() => {
  return !props.readonly && props.multiple && canAddMore.value
})

const isDashedShellClickable = computed(() => {
  return !props.readonly && canAddMore.value
})

function handleDashedShellClick(event: MouseEvent) {
  if (props.readonly || !canAddMore.value) {
    return
  }
  const target = event.target as HTMLElement | null
  if (!target) {
    return
  }
  if (target.closest('.base-input-uploader__item')) {
    return
  }
  handleOpenPicker()
}

function handleFilledSurfaceClick(event: MouseEvent) {
  if (props.readonly || !props.multiple || !canAddMore.value) {
    return
  }
  const target = event.target as HTMLElement | null
  if (!target) {
    return
  }
  if (target.closest('.base-input-uploader__item')) {
    return
  }
  handleOpenPicker()
}

function classifyFile(file: File): FileKind {
  if (file.type.startsWith('image/')) {
    return 'image'
  }
  if (file.type.startsWith('video/')) {
    return 'video'
  }
  return 'file'
}

function classifyMime(mime: string): FileKind {
  if (mime.startsWith('image/')) {
    return 'image'
  }
  if (mime.startsWith('video/')) {
    return 'video'
  }
  return 'file'
}

function isImageFileName(fileName: string): boolean {
  return /\.(?:jpe?g|png|gif|webp|bmp|svg|avif|heic)$/i.test(fileName)
}

function makePreviewUrl(file: File): string {
  return URL.createObjectURL(file)
}

function revokePreviewUrls(items: InternalItem[]) {
  items.forEach((item) => {
    if (item.source === 'local' && item.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(item.previewUrl)
    }
  })
}

function normalizeModelEntries(entries: unknown): BaseInputUploaderModelValue[] {
  if (!Array.isArray(entries)) {
    return []
  }
  if (props.withRemoteResolve) {
    return entries.filter(
      (x): x is BaseInputUploaderModelValue =>
        x instanceof File || isBaseInputUploaderRemotePayload(x),
    )
  }
  return entries.filter((x): x is File => x instanceof File)
}

function reconcileFilteredModel(previous: unknown, normalized: BaseInputUploaderModelValue[]): void {
  if (!Array.isArray(previous) || previous.length === normalized.length) {
    return
  }
  nextTick(() => {
    modelValue.value = normalized
  })
}

function resolveRemotePayload(payload: unknown): BaseInputUploaderRemoteResolved | null {
  const resolve = props.withRemoteResolve
  if (!resolve) {
    return null
  }
  try {
    return resolve(payload)
  }
  catch {
    return {
      previewUrl: '',
      fileName: '?',
      fileSize: null,
    }
  }
}

function buildLocalItem(file: File): InternalLocalItem {
  const kind = classifyFile(file)
  return {
    source: 'local',
    file,
    name: file.name,
    kind,
    previewUrl: kind === 'file' ? '' : makePreviewUrl(file),
  }
}

function buildRemoteItem(remote: BaseInputUploaderRemotePayload): InternalRemoteItem {
  const resolved = resolveRemotePayload(remote.payload)
  const nameRaw = resolved?.fileName?.trim()
  const name = nameRaw != null && nameRaw.length > 0 ? nameRaw : '?'
  const mime = resolved?.mimeType?.trim() ?? ''
  const kindBase = mime
    ? classifyMime(mime)
    : (isImageFileName(name) ? 'image' : 'file')
  const rawUrl = resolved?.previewUrl?.trim() ?? ''
  const kind = rawUrl && kindBase !== 'file' ? kindBase : 'file'
  const previewUrl = rawUrl && kind !== 'file' ? rawUrl : ''

  return {
    source: 'remote',
    remoteId: remote.id,
    name,
    kind,
    previewUrl,
    remoteFileSize: resolved?.fileSize,
  }
}

function buildInternalItems(entries: BaseInputUploaderModelValue[]): InternalItem[] {
  return entries.map((entry) => {
    if (entry instanceof File) {
      return buildLocalItem(entry)
    }
    return buildRemoteItem(entry)
  })
}

function handleDrop(event: DragEvent) {
  if (props.readonly) {
    return
  }
  handleFiles(event.dataTransfer?.files)
}

function handleOpenPicker() {
  if (props.readonly) {
    return
  }
  fileInputRef.value?.click()
}

function handleFileSelect(event: Event) {
  const input = event.target as HTMLInputElement
  if (input.files) {
    handleFiles(input.files)
    input.value = ''
  }
}

function mergeWithExisting(valid: File[]): BaseInputUploaderModelValue[] {
  const current = [...(modelValue.value ?? [])]

  if (!props.withRemoteResolve) {
    return [...current.filter(f => f instanceof File), ...valid]
  }

  return [...current, ...valid]
}

function handleFiles(fileList?: FileList | null) {
  if (!fileList?.length) {
    return
  }

  const incoming = Array.from(fileList)
  const valid: File[] = []

  incoming.forEach((file) => {
    if (props.maxSize && file.size > props.maxSize) {
      notify({
        title: 'Файл слишком большой',
        message: `Файл ${file.name} превышает максимальный размер в ${Math.round(props.maxSize / MB)} МБ`,
        type: 'warning',
      })
      return
    }
    valid.push(file)
  })

  if (!valid.length) {
    return
  }

  let merged = mergeWithExisting(valid)

  if (!props.multiple) {
    merged = [valid[0]!]
  }
  else if (props.maxFiles && merged.length > props.maxFiles) {
    notify({
      title: 'Достигнут лимит',
      message: `Максимум ${props.maxFiles} файлов`,
      type: 'warning',
    })
    merged = merged.slice(0, props.maxFiles)
  }

  modelValue.value = merged
}

function handleRemove(index: number) {
  if (props.readonly) {
    return
  }
  const next = [...(modelValue.value ?? [])]
  next.splice(index, 1)
  modelValue.value = next
}

function openImagePreviewAt(index: number) {
  const item = internalFiles.value[index]
  if (!item || item.kind !== 'image') {
    return
  }

  const imageIndex = internalFiles.value
    .slice(0, index + 1)
    .filter(internalItem => internalItem.kind === 'image')
    .length - 1

  imageViewerSelectedIndex.value = Math.max(0, imageIndex)
  imageViewerVisible.value = true
}

function handleItemClick(index: number) {
  const item = internalFiles.value[index]
  if (!item) {
    return
  }

  if (props.withImagePreview && item.kind === 'image') {
    openImagePreviewAt(index)
    return
  }

  const entries = modelValue.value ?? []
  const entry = entries[index]
  if (entry == null) {
    return
  }

  emit('openItem', entry, index)
}

function resolveInternalItemIndex(item: InternalItem): number {
  return internalFiles.value.findIndex((candidate) => {
    if (item.source === 'local' && candidate.source === 'local') {
      return candidate.file === item.file
    }

    if (item.source === 'remote' && candidate.source === 'remote') {
      return candidate.remoteId === item.remoteId
    }

    return false
  })
}

function handleVisibleItemClick(item: InternalItem) {
  const index = resolveInternalItemIndex(item)
  if (index === -1) {
    return
  }

  handleItemClick(index)
}

function handleVisibleItemRemove(item: InternalItem) {
  const index = resolveInternalItemIndex(item)
  if (index === -1) {
    return
  }

  handleRemove(index)
}

watch(
  [
    () => modelValue.value,
    () => props.withRemoteResolve,
  ],
  ([entries]) => {
    const normalized = normalizeModelEntries(entries)
    reconcileFilteredModel(entries, normalized)

    revokePreviewUrls(internalFiles.value)
    internalFiles.value = buildInternalItems(normalized)
  },
  { deep: true, immediate: true },
)

onBeforeUnmount(() => {
  revokePreviewUrls(internalFiles.value)
})
</script>

<style lang="scss" scoped>
.base-input-uploader {
  width: 100%;

  &__surface {
    width: 100%;
    background-color: var(--color-surface-placeholder);
    border-radius: rem(16px);
    transition: background-color 0.15s ease;

    &--filled {
      box-sizing: border-box;
      max-height: rem(52px);
      padding: rem(6px);
      overflow: hidden;
    }

    &--clickable {
      cursor: pointer;
    }
  }

  &--error {
    .base-input-uploader__surface {
      box-shadow: 0 0 0 1px var(--color-foreground-danger);
    }
  }

  &__native-input {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
    pointer-events: none;
  }

  &__empty {
    width: 100%;
  }

  &__filled-error {
    margin-top: rem(4px);
  }

  &__filled {
    display: flex;
    align-items: center;
    gap: rem(10px);
    width: 100%;
    min-height: 0;
    max-height: 100%;
  }

  &__items {
    display: flex;
    flex: 1;
    flex-wrap: nowrap;
    align-items: center;
    gap: rem(10px);
    min-width: 0;
    max-height: 100%;
    overflow: auto hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }

  &__items::-webkit-scrollbar {
    height: rem(4px);
  }

  &__items::-webkit-scrollbar-thumb {
    border-radius: rem(4px);
    background-color: var(--color-foreground-caption);
  }

  &__item {
    flex: 0 0 auto;

    &--clickable {
      cursor: pointer;
    }
  }

  &__thumb {
    position: relative;
    flex-shrink: 0;
    width: rem(40px);
    height: rem(40px);
    overflow: hidden;
    border-radius: rem(8px);

    &--previewable {
      cursor: pointer;
    }
  }

  &__thumb-img,
  &__thumb-video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__remove {
    flex-shrink: 0;

    &--on-media {
      position: absolute;
      top: rem(4px);
      right: rem(4px);
    }
  }

  &--variant-dashed {
    .base-input-uploader__dashed {
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-height: var(--base-input-uploader-height, #{rem(120px)});
      padding: rem(20px);
      border-radius: var(--radius-sm);
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='20' ry='20' stroke='%23A4A4A4FF' stroke-width='3' stroke-dasharray='16%2c 24' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
      text-align: center;
      transition: background-color 0.15s ease;

      &--filled {
        align-items: stretch;
        justify-content: flex-end;
        text-align: left;
      }
    }

    .base-input-uploader__dashed--clickable {
      cursor: pointer;
    }

    .base-input-uploader__dashed-empty {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
    }

    .base-input-uploader__dashed-filled {
      display: flex;
      align-items: flex-end;
      justify-content: flex-start;
      width: 100%;
      min-height: 0;
    }

    .base-input-uploader__items--dashed {
      flex-wrap: wrap;
      align-items: flex-end;
      align-content: flex-end;
      max-height: none;
      overflow: visible auto;
    }
  }
}
</style>
